import { queryClient } from 'src/query-client'
import { useGet, usePost, usePut } from './reactQuery'
import { maintenanceQueryKeys } from 'src/query-keys'
import { ScheduledMaintenanceFilterParams } from 'src/generated-types/scheduled-maintenance-filter'
import { IScheduledMaintenance } from 'src/generated-types/scheduled-maintenance'
import { IMaintenance } from 'src/generated-types/maintenance'
import { IMainComponent } from 'src/generated-types/main-component'
import { QueryOptions } from 'src/generated-types/query-options'

const baseUrl = (customerId: EntityId, localityId: EntityId) => `customers/${customerId}/localities/${localityId}/maintenances`

export const maintenanceEndpoints = {
  createCustomMaintenance(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  updateCustomMaintenance(customerId: EntityId, localityId: EntityId, maintenanceId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${maintenanceId}`
  },
  getLocalityMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}/locality-maintenances`
  },
  getAllMaintenances(customerId: EntityId, localityId: EntityId) {
    return `${baseUrl(customerId, localityId)}`
  },
  getMaintenancesByCage(customerId: EntityId, localityId: EntityId, cagePositionId: EntityId) {
    return `${baseUrl(customerId, localityId)}/positions/${cagePositionId}/group-by-cage`
  },
  getMaintenancesByEquipmentType(customerId: EntityId, localityId: EntityId, mainComponentTypeId: EntityId) {
    return `${baseUrl(customerId, localityId)}/main-component-types/${mainComponentTypeId}/group-by-equipmentType`
  },
  getScheduledMaintenanceByMaintenance(customerId: EntityId, localityId: EntityId, maintenanceId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${maintenanceId}/scheduled-maintenances`
  },
  getEquipmentByMaintenance(customerId: EntityId, localityId: EntityId, maintenanceId: EntityId) {
    return `${baseUrl(customerId, localityId)}/${maintenanceId}/get-equipment`
  }
}

export const invalidateMaintenanceCache = {
  getLocalityMaintenances(customerId: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_LOCALITY_MAINTENANCES(customerId, localityId, queryParams))
  },
  getAllMaintenances(customerId: EntityId, localityId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_ALL_MAINTENANCES(customerId, localityId, queryParams))
  },
  getMaintenancesByCage(customerId: EntityId, localityId?: EntityId, cagePositionId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_MAINTENANCES_BY_CAGE(customerId, localityId, cagePositionId, queryParams))
  },
  getMaintenancesByEquipment(customerId: EntityId, localityId?: EntityId, mainComponentTypeId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_MAINTENANCES_BY_EQUIPMENT_TYPE(customerId, localityId, mainComponentTypeId, queryParams))
  },
  getMaintenancesByMaintenance(customerId: EntityId, localityId?: EntityId, maintenanceId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_MAINTENANCE(customerId, localityId, maintenanceId, queryParams))
  },
  getEquipmentByMaintenance(customerId: EntityId, localityId?: EntityId, maintenanceId?: EntityId) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_EQUIPMENT_BY_MAINTENANCE(customerId, localityId, maintenanceId))
  },
  invalidateAllMaintenances(customerId: EntityId, localityId?: EntityId, cagePositionId?: EntityId, mainComponentTypeId?: EntityId, maintenanceId?: EntityId, queryParams?: object) {
    invalidateMaintenanceCache.getLocalityMaintenances(customerId, localityId, queryParams)
    invalidateMaintenanceCache.getAllMaintenances(customerId, localityId, queryParams)
    invalidateMaintenanceCache.getMaintenancesByCage(customerId, localityId, cagePositionId, queryParams)
    invalidateMaintenanceCache.getMaintenancesByEquipment(customerId, localityId, mainComponentTypeId, queryParams)
    invalidateMaintenanceCache.getMaintenancesByMaintenance(customerId, localityId, maintenanceId, queryParams)
    invalidateMaintenanceCache.getEquipmentByMaintenance(customerId, localityId, maintenanceId)
  },
  getScheduledMaintenanceByMaintenance(customerId: EntityId, localityId?: EntityId, maintenanceId?: EntityId, queryParams?: object) {
    queryClient.invalidateQueries(maintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_MAINTENANCE(customerId, localityId, maintenanceId, queryParams))
  }
}

export const useAddCustomMaintenance = (customerId: EntityId, localityId: EntityId) =>
  usePost<IMaintenance, IMaintenance>(maintenanceEndpoints.createCustomMaintenance(customerId, localityId))

export const useUpdateCustomMaintenance = (customerId: EntityId, localityId: EntityId, maintenanceId: EntityId) =>
  usePut<IMaintenance, IMaintenance>(maintenanceEndpoints.updateCustomMaintenance(customerId, localityId, maintenanceId))

export const useGetLocalityMaintenances = (customerId: EntityId, localityId: EntityId, onlyDaily = false) =>
  useGet<Array<IMaintenance>>(
    maintenanceEndpoints.getLocalityMaintenances(customerId, localityId),
    { onlyDaily },
    {
      queryKey: maintenanceQueryKeys.GET_LOCALITY_MAINTENANCES(customerId, localityId, { onlyDaily })
    }
  )

export const useGetAllMaintenances = (customerId: EntityId, localityId: EntityId, onlyDaily = false, filterParams?: ScheduledMaintenanceFilterParams, options?: QueryOptions) =>
  useGet<Array<IMaintenance>>(
    maintenanceEndpoints.getAllMaintenances(customerId, localityId),
    { onlyDaily, ...filterParams },
    {
      queryKey: maintenanceQueryKeys.GET_ALL_MAINTENANCES(customerId, localityId, { onlyDaily, ...filterParams }),
      ...options
    }
  )

export const useGetMaintenancesByCage = (
  customerId: EntityId,
  localityId: EntityId,
  cagePositionId: EntityId,
  onlyDaily = false,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: QueryOptions
) =>
  useGet<Array<IMaintenance>>(
    maintenanceEndpoints.getMaintenancesByCage(customerId, localityId, cagePositionId),
    { onlyDaily, ...filterParams },
    {
      queryKey: maintenanceQueryKeys.GET_MAINTENANCES_BY_CAGE(customerId, localityId, cagePositionId, { onlyDaily, ...filterParams }),
      ...options
    }
  )

export const useGetMaintenancesByEquipmentType = (
  customerId: EntityId,
  localityId: EntityId,
  mainComponentTypeId: EntityId,
  onlyDaily = false,
  filterParams?: ScheduledMaintenanceFilterParams,
  options?: QueryOptions
) =>
  useGet<Array<IMaintenance>>(
    maintenanceEndpoints.getMaintenancesByEquipmentType(customerId, localityId, mainComponentTypeId),
    { onlyDaily, ...filterParams },
    {
      queryKey: maintenanceQueryKeys.GET_MAINTENANCES_BY_EQUIPMENT_TYPE(customerId, localityId, mainComponentTypeId, { onlyDaily, ...filterParams }),
      ...options
    }
  )

export const useGetScheduledMaintenanceByMaintenance = (
  customerId: EntityId,
  localityId: EntityId,
  maintenanceId: EntityId,
  isLocality = false,
  onlyDaily = false,
  equipmentTypeId?: EntityId,
  positionId?: EntityId,
  filterParams?: ScheduledMaintenanceFilterParams,
) =>
  useGet<Array<IScheduledMaintenance>>(
    maintenanceEndpoints.getScheduledMaintenanceByMaintenance(customerId, localityId, maintenanceId),
    { isLocality, onlyDaily, equipmentTypeId, positionId, ...filterParams },
    {
      queryKey: maintenanceQueryKeys.GET_SCHEDULED_MAINTENANCES_BY_MAINTENANCE(customerId, localityId, maintenanceId, { onlyDaily, equipmentTypeId, positionId, ...filterParams }),
    }
  )

export const useGetEquipmentByMaintenance = (
  customerId: EntityId,
  localityId: EntityId,
  maintenanceId: EntityId,
  positionId?: EntityId,
  equipmentTypeId?: EntityId,
  options?: QueryOptions
) =>
  useGet<Array<IMainComponent>>(
    maintenanceEndpoints.getEquipmentByMaintenance(customerId, localityId, maintenanceId),
    { positionId, equipmentTypeId },
    {
      queryKey: maintenanceQueryKeys.GET_EQUIPMENT_BY_MAINTENANCE(customerId, localityId, maintenanceId, positionId, equipmentTypeId),
      ...options
    }
  )